<template>
  <v-card>
    <v-card-title class="mt-3">
      <h1 class="ml-2">Sprawozdanie końcowe</h1>
    </v-card-title>
    <v-container fluid>
      <div style="padding-left: 10px; padding-bottom: 6px; display: flex">
        <v-tooltip
          top
          v-if="
            canCancelCorrectionsAndPeekCorrectionCause &&
            reportMetadata.correctionCause
          "
        >
          <template v-slot:activator="{ on }">
            <div class="mr-2">
              <v-icon small v-on="on" style="padding-right: 10px">info</v-icon>
            </div>
          </template>
          <div>
            <b>Sprawozdanie oczekuje na poprawki</b>
          </div>
          <span>{{ reportMetadata.correctionCause }}</span>
        </v-tooltip>
        <h3>Status: {{ reportStatusMessage }}</h3>
      </div>

      <v-btn v-if="!reportVisible && canView" @click="showReport">
        Zobacz podgląd sprawozdania
      </v-btn>

      <v-btn
        v-if="reportVisible && canView"
        @click="hideReport"
        class="no-print"
      >
        Ukryj podgląd sprawozdania
      </v-btn>

      <v-btn v-if="canApprove" color="green" @click="openApprovalDialog">
        Zatwierdź sprawozdanie
      </v-btn>

      <v-btn v-if="canUndoApproval" @click="undoApproval" class="error">
        Cofnij zatwierdzenie sprawozdania
      </v-btn>

      <v-btn v-if="canCorrect" color="orange" @click="openCorrectDialog">
        Oddaj sprawozdanie do poprawek
      </v-btn>

      <v-btn
        v-if="canCancelCorrectionsAndPeekCorrectionCause"
        color="red"
        @click="cancelCorrections"
      >
        Cofnij oddanie do poprawek
      </v-btn>

      <v-btn v-if="exportAvailable" @click="exportCosts">
        Eksportuj tabelę kosztów
      </v-btn>

      <v-btn v-if="canPrintReport" class="no-print" @click="openReportView">
        Wydrukuj sprawozdanie
      </v-btn>

      <v-btn
        v-if="canDownloadSignedReportFile"
        @click="navigateToFinalReportSigned"
      >
        Pobierz podpisane sprawozdanie
      </v-btn>

      <v-btn
        class="error"
        v-if="canDeleteSignedReportFile"
        @click="deleteSignedReportFile"
      >
        Usuń podpisane sprawozdanie
      </v-btn>

      <agency-project-report-approval-dialog
        :dialog.sync="reportApprovalOpen"
        v-on:approve-report="approveReport"
        :currency="currencyCode"
      >
      </agency-project-report-approval-dialog>

      <egranty-input-field-popup
        title="Oddawanie sprawozdania do poprawek"
        :is-open="correctionCauseOpen"
        field-type="LONG_TEXT"
        label="Powód oddania do poprawek"
        additional-info="Uwaga! Jeżeli podpisany plik sprawozdania końcowego został załączony do projektu, to zostanie on usunięty."
        save-button-label="Wyślij"
        @save="correctReport"
        @cancel="correctionCauseOpen=false"
      >
      </egranty-input-field-popup>

      <v-card v-show="reportVisible">
        <!-- v-show, ponieważ ma być zrenderowane, aby wyeksportować koszty -->
        <agency-project-final-report-form
          :key="componentKey"
          ref="reportForm"
          :project-id="projectId"
          :read-only="true"
        />
      </v-card>
      <v-layout><h3>Uwagi</h3></v-layout>
      <v-layout>
        <v-flex>
          <v-textarea
            outline
            auto-grow
            v-model="project.finalReportRemarks"
            browser-autocomplete="off"
            aria-label="final report remarks"
          ></v-textarea>
        </v-flex>
      </v-layout>
      <v-btn primary @click="saveAgencyFinalReportRemarks">Zapisz</v-btn>
    </v-container>
  </v-card>
</template>

<script>
import AgencyProjectFinalReportForm from "./AgencyProjectFinalReportForm";
import EgrantyInputFieldPopup from "../../../genericComponents/EgrantyInputFieldPopup";
import { AgencyFinalReport } from "@/api/generated/AgencyFinalReport";
import { AgencyProject } from "@/api/generated/AgencyProject";
import AgencyProjectReportApprovalDialog from "./components/AgencyProjectReportApprovalDialog";
import { Files } from "@/utils/Files";

export default {
  name: "AgencyProjectFinalReportView",
  components: { AgencyProjectReportApprovalDialog, EgrantyInputFieldPopup, AgencyProjectFinalReportForm },
  mixins: [Files],
  data() {
    return {
      reportApprovalOpen: false,
      correctionCauseOpen: false,
      reportMetadata: null,
      projectMetadata: null,
      reportVisible: false,
      currencyCode: '',
      componentKey: false,
      contestId: this.$route.params.id,
      projectId: this.$route.params.projectId,
      authorizedAdmin: false,
      supervisor: false
    };
  },

  props: {
			project: {
				type: Object
			},
		},

  computed: {
    hasAgencyProjectPanelSupervisorModificationAccess(){
				return this.$store.state.agencyProjectPanelPermissions.hasAgencyProjectPanelSupervisorModificationAccess;
		},
    canDownloadSignedReportFile() {
      return this.reportMetadata && this.reportMetadata.canDownloadSignedReportFile;
    },
    currentReportStatus() {
      if (!this.loaded) {
        return "";
      }
      return this.reportMetadata.status;
    },

    exportAvailable() {
      return this.loaded;
    },

    canApprove() {
      return (this.currentReportStatus === 'SENT' || this.currentReportStatus === 'CHANGES_SENT');
    },

    canUndoApproval() {
      return this.hasAgencyProjectPanelSupervisorModificationAccess && this.currentReportStatus === 'APPROVED';
    },

    canCorrect() {
      return this.currentReportStatus === 'SENT' || this.currentReportStatus === 'CHANGES_SENT';
    },

    canView() {
      return this.currentReportStatus === 'APPROVED' ||
        ( (this.currentReportStatus === 'SENT'
          || this.currentReportStatus === 'CHANGES_SENT'
          || this.currentReportStatus === 'REQUESTED_CHANGES'));
    },

    canCancelCorrectionsAndPeekCorrectionCause() {
      return this.currentReportStatus === 'REQUESTED_CHANGES';
    },

    canPrintReport() {
      return this.currentReportStatus === 'APPROVED' ||
        ( (this.currentReportStatus === 'SENT'
          || this.currentReportStatus === 'CHANGES_SENT'));
    },
    canDeleteSignedReportFile() {
      return this.reportMetadata && this.reportMetadata.canDownloadSignedReportFile;
    },

    reportStatusMessage() {
      if (!this.loaded) {
        return "";
      }
      switch (this.currentReportStatus) {
        case 'NEW':
          return 'Sprawozdanie niezłożone';
        case 'APPROVED':
          return 'Sprawozdanie zatwierdzone';
        case "SENT":
          return 'Sprawozdanie wysłane';
        case 'REQUESTED_CHANGES':
          return 'Sprawozdanie oczekuje na poprawki';
        case 'CHANGES_SENT':
          return 'Sprawozdanie poprawione';
      }
      return "";
    },

    loaded() {
      return this.projectMetadata !== null && this.reportMetadata !== null;
    },

    canEditReport() {
      return this.loaded &&
        (this.projectMetadata.agencyProjectStatus === 'AGREEMENT_SIGNED');
    },
  },

  methods: {
    deleteSignedReportFile() {
      this.$confirm("Czy na pewno chcesz usunąć plik z podpisanym sprawozdaniem?", {
        color: "blue",
        icon: "warning",
        title: "Usunięcie pliku z podpisanym sprawozdaniem",
        width: 600,
      })
        .then(response => {
          if (response) {
            AgencyProject.deleteSignedReportFile(this, this.projectId, successResponse => {
              this.$messagesBus.$emit("success", "Plik z podpisanym sprawozdaniem został usunięty", successResponse);
              this.componentKey = !this.componentKey; // used to rerender the component
              this.refresh();
            }, errorResponse => {
              this.$messagesBus.$emit("error", "Nie udało się usunąć pliku z podpisanym sprawozdaniem", errorResponse);
            });
          }
        });
    },
    async navigateToFinalReportSigned() {
      await this.downloadFileAsBlob(this, this.getAgencyProjectSignedProjectFile(this.projectId), this.reportMetadata.signedReportFileName);
    },
    cancelCorrections() {
      AgencyFinalReport.postCancelCorrections(
        this,
        this.projectId,
        () => {
          this.$messagesBus.$emit("success", "Cofnięto oddanie sprawozdania do poprawek");
          this.componentKey = !this.componentKey;
          this.refresh();
        },
        errorResponse => {
          this.$messagesBus.$emit("error", "Nie udało się cofnąć oddania sprawozdania do poprawek", errorResponse);
        }
      );
    },

    exportCosts() {
      this.$refs.reportForm.generateExport();
    },

    openReportView() {
      this.$router.openInNewTab('/view/agency-contest/' + this.contestId + '/final-report/' + this.projectId);
    },

    openApprovalDialog() {
      this.reportApprovalOpen = true;
    },

    openCorrectDialog() {
      this.correctionCauseOpen = true;
    },

    undoApproval() {
      this.$confirm("Czy na pewno chcesz cofnąć zatwierdzenie sprawozdania? Cofnięcie spowoduje usunięcie podanych kwot.", {
        color: "blue",
        icon: "warning",
        title: "Cofnięcie zatwierdzenia sprawozdania",
        width: 600,
      })
        .then(response => {
          if (response) {
            AgencyFinalReport.putUndoApproval(this, this.projectId, successResponse => {
              this.$messagesBus.$emit("success", "Zatwierdzenie sprawozdania zostało cofnięte", successResponse);
              this.componentKey = !this.componentKey; // used to rerender the component
              this.refresh();
            }, errorResponse => {
              this.$messagesBus.$emit("error", "Nie udało się cofnąć zatwierdzenia sprawozdania", errorResponse);
            });
          }
        });
    },

    approveReport(data) {
      AgencyFinalReport.postApprove(
        this,
        this.projectId,
        data,
        () => {
          this.reportApprovalOpen = false;
          this.componentKey = !this.componentKey; // used to rerender the component
          this.refresh();
        },
        errorResponse => {
          this.$messagesBus.$emit("error", errorResponse.errors[0], errorResponse);
        }
      );
    },

    hideReport() {
      this.reportVisible = false;
    },

    showReport() {
      this.componentKey = !this.componentKey;
      this.reportVisible = true;
    },

    correctReport(cause) {
      this.correctionCauseOpen = false;
      AgencyFinalReport.postRequestCorrections(
        this,
        this.projectId,
        cause,
        () => {
          this.$messagesBus.$emit("success", "Sprawozdanie zostało oddane do poprawek");
          this.componentKey = !this.componentKey;
          this.refresh();
        },
        errorResponse => {
          this.$messagesBus.$emit("error", "Nie udało się oddać sprawozdania do poprawek", errorResponse);
        }
      );
    },

    saveAgencyFinalReportRemarks(){
      AgencyProject.putFinalReportRemarks(this, this.$route.params.projectId, {body: this.project.finalReportRemarks});
    },

    refresh() {
      AgencyProject.getBasicData(
        this,
        this.projectId,
        response => this.projectMetadata = response
      );
      AgencyFinalReport.getCurrentState(
        this,
        this.projectId,
        response => this.reportMetadata = response
      );
      AgencyProject.getCurrencyCode(this, this.projectId, successResponse => {
        this.currencyCode = successResponse.code;
      },
        errorResponse => {
          this.$messagesBus.$emit("error", "Nie udało się załadować waluty", errorResponse);
        });
      AgencyFinalReport.getReportManagementStatus(
        this,
        this.projectId,
        response => {
          this.authorizedAdmin = response.authorizedAdmin;
          this.supervisor = response.supervisor;
        }
      );
    }
  },

  mounted() {
    this.refresh();
  }
};
</script>

<style scoped>
</style>
